import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Button, ButtonVariant } from "@src/components/button/Button";
import { DateInput } from "@src/components/input/DateInput";
import { Input } from "@src/components/input/Input";
import { SelectYup } from "@src/components/select/SelectYup";
import { Switch } from "@src/components/switch/Switch";
import { getEventIdOptions } from "@src/components/utils/reactSelectUtils";
import { useAppSelector } from "@src/store/hooks";
import styles from "./DuplicateListing.module.scss";

export interface DuplicateFilterValues {
	search: string;
	startDate: string;
	endDate: string;
	eventId: string;
	categoryId: string | number;
	headlinerId: string | number;
	venueId: string | number;
	includeParking: boolean;
}

type FormShape = Omit<DuplicateFilterValues, "includeParking">;

const emptyValues: FormShape = {
	search: "",
	startDate: "",
	endDate: "",
	eventId: "",
	categoryId: "",
	headlinerId: "",
	venueId: "",
};

interface DuplicateFiltersProps {
	onFilter: (values: DuplicateFilterValues) => void;
	loading?: boolean;
}

export const DuplicateFilters: React.FC<DuplicateFiltersProps> = ({ onFilter, loading }) => {
	const { control, handleSubmit, reset } = useForm<FormShape>({ defaultValues: emptyValues });
	const [includeParking, setIncludeParking] = useState(false);

	const events = useAppSelector((state) => state.pricing.events.data.Events);

	const categoryOptions = useMemo(() => getEventIdOptions(events, "Cat_Desc", "Cat_ID"), [events]);
	const headlinerOptions = useMemo(() => getEventIdOptions(events, "Event_Headliner", "Event_Headliner_ID"), [events]);
	const venueOptions = useMemo(() => getEventIdOptions(events, "Venue_Name", "Venue_ID"), [events]);

	const submit = (data: FormShape) => onFilter({ ...data, includeParking });

	const handleReset = () => {
		reset(emptyValues);
		setIncludeParking(false);
	};

	return (
		<div className={styles.filters}>
			<fieldset className={styles.fieldset}>
				<legend className={styles.legend}>Search</legend>
				<Input control={control} name="search" placeholder="Search" controlSize="sm" disableErrorMessage />
				<DateInput control={control} name="startDate" placeholder="Event Start Date" controlSize="sm" />
				<DateInput control={control} name="endDate" placeholder="Event End Date" controlSize="sm" />
				<Input
					control={control}
					name="eventId"
					placeholder="Event ID"
					type="number"
					controlSize="sm"
					disableErrorMessage
				/>
			</fieldset>

			<fieldset className={styles.fieldset}>
				<legend className={styles.legend}>Filters</legend>
				<SelectYup control={control} name="categoryId" options={categoryOptions} placeholder="Choose Category" />
				<SelectYup control={control} name="headlinerId" options={headlinerOptions} placeholder="Choose Headliner" />
				<SelectYup control={control} name="venueId" options={venueOptions} placeholder="Choose Venue" />
				<div className={styles.parkingRow}>
					<span className={styles.parkingLabel}>Include Parking:</span>
					<Switch checked={includeParking} onChange={(checked) => setIncludeParking(checked)} />
				</div>
			</fieldset>

			<div className={styles.filterButtons}>
				<Button onClick={handleReset} variant={ButtonVariant.Secondary} controlSize="sm">
					Reset
				</Button>
				<Button onClick={handleSubmit(submit)} isLoading={loading} variant={ButtonVariant.Primary} controlSize="sm">
					Filter
				</Button>
			</div>
		</div>
	);
};
